/*  iceberg-ui  */
import { IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import {
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Grid,
    Typography
} from '@mui/material';

/*  Shared  */
import { DarkTooltip } from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';
import { Notify } from 'application/Shared/Helpers';
import { Confirm } from 'application/Shared/Modals/Confirm';

/*  Hooks  */
import {
    usePostRetroactivelyRedact,
    usePurgeTenant
} from 'hooks/dataAccess/Tenant/tenant';

/*  React  */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CustomAccordion } from './Shared/CustomAccordion';

const RequestRedaction = ({ tenant }) => {
    const { t } = useTranslation();
    const { redactRetroactively } = usePostRetroactivelyRedact(t);

    const [showRedactionConfirm, setShowRedactionConfirm] = useState(false);

    // Only show redaction for client level tenants.
    if (tenant.partner) return null;

    const hidePersonalEmail = tenant?.settings?.hidePersonalEmail || false;

    const promptConfirmFn = async () => {
        await redactRetroactively(tenant.id);
        setShowRedactionConfirm(false);
    };

    const button = (
        <Button
            data-trackid="globalAdmin_tenant_tenantDangerZone_redact"
            onClick={() => setShowRedactionConfirm(true)}
            color="primary"
            variant="contained"
            disabled={!hidePersonalEmail}
        >
            {t('GlobalAdmin.Tenant.Edit.redaction.button')}
        </Button>
    );

    return (
        <>
            <Grid item xs={12}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Box>
                        <Typography sx={{ fontWeight: 600 }}>
                            {t('GlobalAdmin.Tenant.Edit.redaction.title')}
                        </Typography>
                        <Typography>
                            {t('GlobalAdmin.Tenant.Edit.redaction.description')}
                        </Typography>
                    </Box>
                    {hidePersonalEmail ? (
                        button
                    ) : (
                        <DarkTooltip
                            arrow
                            placement="left"
                            title={t(
                                'GlobalAdmin.Tenant.Edit.redaction.notEnabledTooltip'
                            )}
                        >
                            <span>{button}</span>
                        </DarkTooltip>
                    )}
                    {hidePersonalEmail && showRedactionConfirm && (
                        <Confirm
                            modalOpen={!!showRedactionConfirm}
                            promptConfirmFn={promptConfirmFn}
                            promptCancelFn={() =>
                                setShowRedactionConfirm(false)
                            }
                            promptTitle={t(
                                'GlobalAdmin.Tenant.Edit.redaction.title'
                            )}
                            promptText={t(
                                'GlobalAdmin.Tenant.Edit.redaction.confirm.description'
                            )}
                        />
                    )}
                </Box>
            </Grid>

            <Grid item xs={12}>
                <hr />
            </Grid>
        </>
    );
};

const PurgeTenant = ({ tenant, onSuccessFn }) => {
    const { t } = useTranslation();
    const { purgeTenant } = usePurgeTenant(t);

    const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);

    const promptConfirmFn = async () => {
        const response = await purgeTenant(tenant.id);
        if (response) {
            Notify.s(t('GlobalAdmin.Tenant.Edit.purgeSubmitted'));
        } else {
            Notify.e(t('GlobalAdmin.Tenant.Edit.purgeError'));
        }
        setShowPurgeConfirm(false);
        onSuccessFn();
    };

    const text = tenant.partner
        ? t('GlobalAdmin.Tenant.Edit.purge.descriptionPartner')
        : t('GlobalAdmin.Tenant.Edit.purge.descriptionClient');

    return (
        <Grid item xs={12}>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <Box>
                    <Typography sx={{ fontWeight: 600 }}>
                        {t('GlobalAdmin.Tenant.Edit.purge.title')}
                    </Typography>
                    <Typography>
                        {t('GlobalAdmin.Tenant.Edit.purge.description')}
                    </Typography>
                </Box>
                <Button
                    data-trackid="globalAdmin_tenant_tenantDangerZone_purge"
                    onClick={() => setShowPurgeConfirm(true)}
                    color="primary"
                    variant="contained"
                >
                    {t('GlobalAdmin.Tenant.Edit.purge.button')}
                </Button>
                {showPurgeConfirm && (
                    <Confirm
                        modalOpen={!!showPurgeConfirm}
                        promptConfirmFn={promptConfirmFn}
                        promptCancelFn={() => setShowPurgeConfirm(false)}
                        promptTitle={t('GlobalAdmin.Tenant.Edit.purge.title')}
                        promptText={text}
                    />
                )}
            </Box>
        </Grid>
    );
};

export const TenantDangerZone = ({ tenant, onSuccessFn }) => {
    const { t } = useTranslation();

    return (
        <CustomAccordion initialExpanded={false}>
            <AccordionSummary
                expandIcon={
                    <IconWithVariant
                        data-testid="expandAccordionIconTenantDangerZone"
                        variant="primary"
                        src="ChevronDown"
                        size={FontSizes[2]}
                    />
                }
            >
                <Box display="flex" alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t('GlobalAdmin.Tenant.Edit.dangerZone.title')}
                    </Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container>
                    <Grid item xs={12} mb="32px">
                        <Alert severity="error">
                            {t('GlobalAdmin.Tenant.Edit.dangerZone.warning')}
                        </Alert>
                    </Grid>

                    <RequestRedaction tenant={tenant} />
                    <PurgeTenant tenant={tenant} onSuccessFn={onSuccessFn} />
                </Grid>
            </AccordionDetails>
        </CustomAccordion>
    );
};
